import {intlOverrides} from "@irati-web/app/src/phrase"
import {LangProvider} from "@irati/utils/lang/Provider"
import getPhraseBucket from "@irati/utils/lang/service/request"
import React, {FC} from "react"
import {IntlProvider, IntlShape} from "react-intl"
import styled, {ThemeProvider, DefaultTheme} from "styled-components"

export enum OverlayPlacement {
  Right = "right",
  Left = "left",
  Bottom = "bottom",
  Top = "top"
}

export interface OverlayProps {
  id?: string
  placement?: OverlayPlacement
  theme: DefaultTheme
  intl: IntlShape
}

const StyledOverlay = styled.div<Pick<OverlayProps, "placement">>`
  ${({theme, placement}) => `
    position: absolute;
    background-color: ${theme.colors.utility.white};
    padding: ${theme.spacing.one};
    border-radius: ${theme.border.radius.large};
    max-width: 250px;
    z-index: 99999;
    box-shadow: ${theme.shadows.high};

    ${placement === OverlayPlacement.Right && "transform: translate3d(10px, 0, 0);"}
    ${placement === OverlayPlacement.Left && "transform: translate3d(-10px, 0, 0);"}
    ${placement === OverlayPlacement.Bottom && "transform: translate3d(0, 10px, 0);"}
    ${placement === OverlayPlacement.Top && "transform: translate3d(0, -10px, 0);"}

    :before {
      border: solid transparent;
      content: " ";
      height: 0;
      width: 0;
      position: absolute;
      pointer-events: none;
      border-width: 7px;

      ${
        placement === OverlayPlacement.Right &&
        `
        top: 50%;
        right: 100%;
        margin-top: -7px;
        border-right-color: ${theme.colors.utility.white};
      `
      }
      ${
        placement === OverlayPlacement.Left &&
        `
        top: 50%;
        left: 100%;
        margin-top: -7px;
        border-left-color: ${theme.colors.utility.white};
      `
      }
      ${
        placement === OverlayPlacement.Bottom &&
        `
        bottom: 100%;
        left: 50%;
        margin-left: -7px;
        border-bottom-color: ${theme.colors.utility.white};
      `
      }
      ${
        placement === OverlayPlacement.Top &&
        `
        top: 100%;
        left: 50%;
        margin-left: -7px;
        border-top-color: ${theme.colors.utility.white};
      `
      }
    }
  `}
`

// This component is being rendered by react-bootstrap OverlayTrigger component.
// It looks like it's not giving it access to the main theme provider so we are providing it again.
const Overlay: FC<OverlayProps> = ({theme, intl, ...props}) => {
  return (
    <ThemeProvider theme={theme}>
      <LangProvider textComponent="span" intlOverrides={intlOverrides} contentFetcher={getPhraseBucket}>
        <IntlProvider {...intl}>
          <StyledOverlay {...props} />
        </IntlProvider>
      </LangProvider>
    </ThemeProvider>
  )
}

export default Overlay
